// Frontend entry point for fm-user-tracking-klaviyo.
// Plain ES5+ vanilla — no DOM lib deps.
//
// IMPORTANT: do NOT touch `window.klaviyo` from this file. Klaviyo's onsite
// snippet uses `if (!window.klaviyo) { install Proxy }`, so if we initialise
// `window.klaviyo = []` ourselves we win the race and klaviyo.js silently
// skips its own setup — every subsequent push then piles into an orphan array
// that nobody drains. Instead we poll for klaviyo.identify to become a real
// function (which means the Proxy is installed) and call the modern API
// directly: klaviyo.identify({...}) / klaviyo.track(name, props).
//
// DEBUG LOGGING
// =============
// Silent by default. Turn on with EITHER:
//   - URL:          add ?fm_utk_debug=1   (persists; clears with ?fm_utk_debug=0)
//   - localStorage: localStorage.setItem('fm_utk_debug', '1')
//   - Runtime:      window.FM_UTK_DEBUG = true
// Then reload. Look for `[fm-utk]` rows in the DevTools console.

(function () {
    'use strict';

    if (typeof window === 'undefined' || !window.FM_UTK) {
        return;
    }

    var ctx = window.FM_UTK;
    var events = ctx.eventsEnabled || {};

    // --- Debug logging ------------------------------------------------------
    var DEBUG = false;
    try {
        var qsDebug = (location.search.match(/[?&]fm_utk_debug=([^&]*)/) || [])[1];
        if (qsDebug === '1') {
            localStorage.setItem('fm_utk_debug', '1');
        } else if (qsDebug === '0') {
            localStorage.removeItem('fm_utk_debug');
        }
        DEBUG = window.FM_UTK_DEBUG === true || localStorage.getItem('fm_utk_debug') === '1';
    } catch (e) { /* localStorage blocked — DEBUG stays false */ }

    function dbg() {
        if (!DEBUG) return;
        var args = ['[fm-utk]'];
        for (var i = 0; i < arguments.length; i++) args.push(arguments[i]);
        try { console.log.apply(console, args); } catch (e) {}
    }
    function dbgGroup(label, payload) {
        if (!DEBUG) return;
        try {
            console.groupCollapsed('[fm-utk] ' + label);
            console.log(payload);
            console.groupEnd();
        } catch (e) {}
    }

    // --- Klaviyo-ready gate -------------------------------------------------
    // klaviyo.js installs `window.klaviyo` as a Proxy whose every property is
    // a function. We treat "klaviyo.identify is a real function (not an array
    // method) and window.klaviyo is not an Array" as the ready signal.
    function isKlaviyoReady() {
        var k = window.klaviyo;
        if (!k || Array.isArray(k)) return false;
        return typeof k.identify === 'function' && typeof k.track === 'function';
    }

    var pendingQueue = [];
    var readyFired = false;
    var pollStart = Date.now();
    var POLL_MS = 50;
    var POLL_TIMEOUT_MS = 10000;

    // Sequential chain — each queued callback waits for the previous one's
    // Promise (if any) to resolve before running. klaviyo.identify() returns
    // a Promise that resolves once the email has been associated with the
    // __kla_id cookie on Klaviyo's server. Without chaining, an identify
    // followed by a track fires the track HTTP request before that
    // association has happened, and Klaviyo attributes the event to the
    // anonymous profile instead of the identified one. Chaining fixes that.
    var chain = (typeof Promise !== 'undefined') ? Promise.resolve() : null;

    function enqueueSequential(fn) {
        if (!chain) {
            // No Promise support — fall back to immediate sync call.
            try { fn(window.klaviyo); } catch (e) { dbg('sync call threw:', e); }
            return;
        }
        chain = chain.then(function () {
            try {
                var result = fn(window.klaviyo);
                // If the callback's call to klaviyo.identify/track returned a
                // Promise, return it so the next chained callback waits on it.
                if (result && typeof result.then === 'function') {
                    return result;
                }
            } catch (e) { dbg('queued call threw:', e); }
        });
    }

    function drainPending() {
        readyFired = true;
        dbg('klaviyo.js is ready — flushing ' + pendingQueue.length + ' queued call(s)');
        var batch = pendingQueue.slice();
        pendingQueue.length = 0;
        batch.forEach(enqueueSequential);
    }

    function whenKlaviyoReady(fn) {
        if (readyFired) {
            enqueueSequential(fn);
            return;
        }
        pendingQueue.push(fn);
    }

    (function pollForKlaviyo() {
        if (isKlaviyoReady()) {
            drainPending();
            return;
        }
        if (Date.now() - pollStart > POLL_TIMEOUT_MS) {
            dbg(
                'TIMEOUT: klaviyo.js never installed its Proxy after ' +
                POLL_TIMEOUT_MS + 'ms — dropped ' + pendingQueue.length + ' queued call(s). ' +
                'Check: company ID in Forms → Tracking, Network tab for static.klaviyo.com/onsite/js/klaviyo.js, ' +
                'and Klaviyo → Account → Settings → API Keys (onsite tracking enabled?).'
            );
            return;
        }
        setTimeout(pollForKlaviyo, POLL_MS);
    })();

    // Convenience wrappers — log + queue.
    function safeIdentify(payload) {
        dbgGroup('queued identify (will fire when klaviyo.js ready)', payload);
        whenKlaviyoReady(function (k) {
            dbgGroup('→ klaviyo.identify()', payload);
            try {
                // Return the Promise so the chain awaits the identify before
                // running the next callback (typically a track). Without this,
                // klaviyo.js sends the track HTTP request before the email →
                // __kla_id association lands on Klaviyo's server and the event
                // gets attributed to an anonymous profile instead.
                return k.identify(payload);
            } catch (e) { dbg('identify threw:', e); }
        });
    }
    function safeTrack(eventName, payload) {
        dbgGroup('queued track "' + eventName + '" (will fire when klaviyo.js ready)', payload);
        whenKlaviyoReady(function (k) {
            dbgGroup('→ klaviyo.track("' + eventName + '")', payload);
            try {
                return k.track(eventName, payload);
            } catch (e) { dbg('track threw:', e); }
        });
    }

    dbgGroup('boot — FM_UTK context', ctx);
    dbg('klaviyo.js ready state at boot: ' + (isKlaviyoReady() ? 'READY' : 'NOT YET — will poll'));
    if (!ctx.klaviyoCompanyId) {
        dbg('WARNING: no Klaviyo company ID configured (Forms → Tracking). klaviyo.js was not loaded, so pushes will time out.');
    }

    // --- Helpers ------------------------------------------------------------

    function readCookie(name) {
        var match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[\.$?*|{}\(\)\[\]\\\/\+^]/g, '\\$&') + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    }

    function deleteCookie(name) {
        document.cookie = name + '=; Max-Age=0; path=/';
    }

    function setCookie(name, value, days) {
        var expires = '';
        if (days) {
            var d = new Date(Date.now() + days * 864e5);
            expires = '; expires=' + d.toUTCString();
        }
        var secure = location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax' + secure;
    }

    function uaProps() {
        var ua = (navigator.userAgent || '').toLowerCase();
        var device = /mobile|iphone|ipod|android.*mobile|opera mini/.test(ua) ? 'mobile'
            : (/ipad|tablet|android(?!.*mobile)/.test(ua) ? 'tablet' : 'desktop');
        var browser = /edg\//.test(ua) ? 'edge'
            : /chrome\//.test(ua) ? 'chrome'
            : /firefox\//.test(ua) ? 'firefox'
            : /safari\//.test(ua) ? 'safari'
            : 'other';
        var os = /windows/.test(ua) ? 'windows'
            : /mac os x/.test(ua) ? 'mac'
            : /android/.test(ua) ? 'android'
            : /iphone|ipad|ipod/.test(ua) ? 'ios'
            : /linux/.test(ua) ? 'linux'
            : 'other';
        return { Device: device, Browser: browser, OS: os };
    }

    function geoProps() {
        var g = ctx.geo;
        if (!g) return {};
        return {
            City: g.city || null,
            Region: g.region || null,
            Country: g.country || null,
            Timezone: g.timezone || null,
        };
    }

    function siteProps() {
        return ctx.site ? { Site: ctx.site } : {};
    }

    // Tag every event with the campaign so a visitor who lands on /article-A via a
    // campaign link, then navigates to /article-B, still carries CampaignId. Prefer
    // the session cookie (shared across tabs, cleared on browser close); fall back to
    // sessionStorage (per-tab) if no cookie is present.
    function campaignProps() {
        try {
            var c = readCookie(CID_CAMPAIGN_COOKIE);
            if (!c) { try { c = sessionStorage.getItem('fm_utk_campaign'); } catch (e) {} }
            if (c) return { CampaignId: c };
        } catch (e) {}
        return {};
    }

    // Strip named params from the address bar via history.replaceState so
    // they don't survive copy-paste, screenshots, or referer headers.
    function stripParamsFromUrl(names) {
        try {
            var url = new URL(location.href);
            var changed = false;
            names.forEach(function (n) {
                if (url.searchParams.has(n)) { url.searchParams.delete(n); changed = true; }
            });
            if (changed) {
                var qs = url.searchParams.toString();
                history.replaceState(null, '', url.pathname + (qs ? '?' + qs : '') + url.hash);
            }
        } catch (e) { dbg('stripParamsFromUrl failed:', e); }
    }

    function parseQuery(qs) {
        var out = {};
        if (!qs) return out;
        qs = qs.replace(/^\?/, '');
        qs.split('&').forEach(function (pair) {
            if (!pair) return;
            var eq = pair.indexOf('=');
            var k = eq === -1 ? pair : pair.slice(0, eq);
            var v = eq === -1 ? '' : pair.slice(eq + 1);
            try { out[decodeURIComponent(k)] = decodeURIComponent(v.replace(/\+/g, ' ')); }
            catch (e) { out[k] = v; }
        });
        return out;
    }

    // First-touch attribution — stored in localStorage on the first page of
    // the *first* session that has UTM params or a non-empty referrer. Once
    // set it's never overwritten, so it captures the original acquisition.
    function ensureFirstTouch() {
        var key = 'fm_utk_first_touch';
        var stored;
        try { stored = JSON.parse(localStorage.getItem(key) || 'null'); } catch (e) { stored = null; }
        if (stored) {
            dbgGroup('first-touch (already stored)', stored);
            return stored;
        }

        var q = parseQuery(location.search);
        var ft = {
            FirstReferrer: document.referrer || null,
            FirstLanding: location.href,
            FirstTouchAt: Math.floor(Date.now() / 1000),
            utm_source: q.utm_source || null,
            utm_medium: q.utm_medium || null,
            utm_campaign: q.utm_campaign || null,
            utm_term: q.utm_term || null,
            utm_content: q.utm_content || null,
        };
        try { localStorage.setItem(key, JSON.stringify(ft)); } catch (e) {}
        dbgGroup('first-touch (new)', ft);
        return ft;
    }

    var firstTouch = ensureFirstTouch();

    // The landing page-view must fire AFTER any identity has been queued, so the
    // sequential chain (see enqueueSequential) attributes it to the resolved
    // profile rather than the anonymous one. For a synchronous identity (stored
    // cookie / fm_utk_email) that ordering is automatic — drainCampaignParams runs
    // before the view block below. But a `?c_id=` identity resolves via an async
    // XHR, so its identify is queued only in the callback, AFTER the view would
    // otherwise have fired. identityPending signals that case; the view is then
    // fired from the resolve callback instead. fireViewEventOnce() is idempotent so
    // the callback + safety timeout can both call it without double-firing.
    var identityPending = false;
    var viewEventFired = false;
    function fireViewEventOnce() {
        if (viewEventFired) return;
        viewEventFired = true;
        if (events.viewedArticle !== false) {
            var commonProps = Object.assign({}, geoProps(), uaProps(), firstTouch, siteProps(), campaignProps());
            var fired = fireViewEvent(commonProps);
            if (!fired && DEBUG) {
                dbg('no view event fired for pageType=' + ctx.pageType + ' (no matching context)');
            }
        } else if (DEBUG) {
            dbg('view events disabled in settings (Forms → Tracking)');
        }
    }

    // Persist a campaign-link identity (resolved email) for a logged-out visitor
    // so they stay identified as they navigate, without keeping the raw id/email
    // in the URL. base64-encoded to match the fm_kt_pending_identify convention.
    // Session cookies (no expiry) — identity + campaign attribution last only for
    // the current browsing session and clear when the browser closes. Deliberately
    // NOT persistent: a campaign click must not attribute a visitor's organic return
    // days later (that would give wrong campaign data). Shared across tabs within the
    // session (unlike sessionStorage, which is per-tab); the campaign is read by
    // campaignProps() onto every page-view event.
    var CID_COOKIE = 'fm_utk_cid_email';
    var CID_CAMPAIGN_COOKIE = 'fm_utk_cid_campaign';

    // Attribution fields (campaign + utm + landing/referrer) worth capturing for
    // Klaviyo segmentation. Read this-touch from the current URL/referrer.
    function campaignAttribution(qs, campaign) {
        return {
            cmp:          campaign || '',
            landing:      ctx.pageUrl || location.href || '',
            ref:          document.referrer || '',
            utm_source:   qs.utm_source || '',
            utm_medium:   qs.utm_medium || '',
            utm_campaign: qs.utm_campaign || '',
            utm_term:     qs.utm_term || '',
            utm_content:  qs.utm_content || ''
        };
    }

    // Resolve a Klaviyo profile id (?c_id=) to its email via the server-side
    // fm_utk_resolve_profile endpoint (the private API key never touches the client).
    // `attr` (campaign + utm + landing/referrer) is forwarded so the server-side
    // Campaign Link Visit event + profile carry attribution for segmentation.
    // cb receives the email string, or null on any failure.
    function resolveProfileEmail(id, attr, cb) {
        if (!ctx.ajaxUrl) { dbg('no ajaxUrl in FM_UTK context — cannot resolve c_id'); cb(null); return; }
        try {
            var xhr = new XMLHttpRequest();
            xhr.open('POST', ctx.ajaxUrl, true);
            xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8');
            xhr.onreadystatechange = function () {
                if (xhr.readyState !== 4) return;
                var email = null;
                try {
                    var res = JSON.parse(xhr.responseText);
                    if (res && res.success && res.data && res.data.email) email = res.data.email;
                } catch (e) { dbg('fm_utk_resolve_profile bad JSON:', e); }
                cb(email);
            };
            var body = 'action=fm_utk_resolve_profile&id=' + encodeURIComponent(id);
            if (attr) {
                for (var k in attr) {
                    if (attr.hasOwnProperty(k) && attr[k]) {
                        body += '&' + encodeURIComponent(k) + '=' + encodeURIComponent(attr[k]);
                    }
                }
            }
            xhr.send(body);
        } catch (e) { dbg('fm_utk_resolve_profile xhr threw:', e); cb(null); }
    }

    // Queue an identify for a campaign-link email and persist it to CID_COOKIE.
    function identifyCampaignEmail(email) {
        setCookie(CID_COOKIE, btoa(email)); // session cookie — no expiry
        var payload = { email: email };
        if (ctx.site) payload.$append = { Sites: ctx.site };
        safeIdentify(payload);
    }

    // 0. Campaign-link URL params (e.g. Klaviyo email campaign clicks).
    //    `c_id`            — opaque Klaviyo profile id ({{ person.id }}) → resolved
    //                        server-side to an email, then identify + persist to
    //                        CID_COOKIE. Keeps the raw email out of the URL/logs.
    //    `fm_utk_email`    — base64-encoded recipient email (legacy/internal links)
    //                        → identify + persist. Same login rule as c_id.
    //    Both identity paths run ONLY when the visitor isn't already logged in via
    //    fm-subs-authoriser. If a subscriber JWT is present, the logged-in email is
    //    authoritative and we skip the URL identify entirely (firing both back-to-
    //    back even with the same email can make klaviyo.js drop the next track
    //    event). When no identity param is present, we re-identify from CID_COOKIE
    //    so the visitor stays identified across navigation. Exactly one identify
    //    fires per page load.
    //    `fm_utk_campaign` — opaque campaign identifier → session-persisted.
    //                        Always read, regardless of login state — the
    //                        campaign attribution applies to the visit, not
    //                        the identity.
    //    Identity/campaign params are stripped from the URL after read so they
    //    don't leak via copy-paste / screenshots / referer headers.
    (function drainCampaignParams() {
        var qs = parseQuery(location.search);
        var emailParam = qs.fm_utk_email;
        var idParam = qs.c_id;
        // Campaign source order: explicit ?cmp=, then Klaviyo's auto ?utm_campaign=,
        // then the legacy param. Whichever is present tags the visitor's journey.
        var campaignParam = qs.cmp || qs.utm_campaign || qs.fm_utk_campaign;
        var alreadyLoggedIn = !!(ctx.subscriber && ctx.subscriber.email);

        if (alreadyLoggedIn) {
            if (emailParam || idParam) {
                dbg('URL-param identity present but subscriber is logged in (' + ctx.subscriber.email + ') — skipping URL identify, JWT identity wins');
            }
            // Drop any stale anonymous identity — the JWT is authoritative now.
            deleteCookie(CID_COOKIE);
        } else if (idParam) {
            dbg('URL-param profile id detected (no JWT):', idParam);
            // Defer the landing page-view until the resolve returns, so it's
            // queued AFTER the identify and attributes to the resolved profile.
            identityPending = true;
            resolveProfileEmail(idParam, campaignAttribution(qs, campaignParam), function (email) {
                if (email && email.indexOf('@') > 0) {
                    dbg('c_id resolved to email:', email);
                    identifyCampaignEmail(email);
                } else {
                    dbg('c_id did not resolve to an email, skipping');
                }
                // Fire the deferred view now — after identify has been queued.
                fireViewEventOnce();
            });
            // Safety net: if the resolve XHR hangs (no timeout is set on it), fire
            // the view anyway so the landing is never lost. Idempotent with the
            // callback above — whichever runs first wins.
            setTimeout(fireViewEventOnce, 3000);
        } else if (emailParam) {
            try {
                var decoded = atob(emailParam);
                if (decoded && decoded.indexOf('@') > 0) {
                    dbg('URL-param email identifier detected (no JWT):', decoded);
                    identifyCampaignEmail(decoded);
                } else {
                    dbg('fm_utk_email decoded value is not an email, skipping:', decoded);
                }
            } catch (e) { dbg('fm_utk_email decode failed:', e); }
        } else {
            // No identity param this page — re-identify from a stored campaign
            // cookie so the known email persists across navigation.
            var stored = readCookie(CID_COOKIE);
            if (stored) {
                try {
                    var storedEmail = atob(stored);
                    if (storedEmail && storedEmail.indexOf('@') > 0) {
                        dbg('re-identifying from stored c_id cookie:', storedEmail);
                        var p = { email: storedEmail };
                        if (ctx.site) p.$append = { Sites: ctx.site };
                        safeIdentify(p);
                    }
                } catch (e) { dbg('c_id cookie malformed, clearing'); deleteCookie(CID_COOKIE); }
            }
        }

        if (campaignParam) {
            // Session cookie (cross-tab, this browser session only) + sessionStorage
            // so the campaign travels with the visitor for the current session but
            // does not persist to a later organic visit.
            setCookie(CID_CAMPAIGN_COOKIE, campaignParam); // session cookie — no expiry
            try {
                sessionStorage.setItem('fm_utk_campaign', campaignParam);
                dbg('campaign tag stored (session cookie + sessionStorage):', campaignParam);
            } catch (e) { dbg('sessionStorage write failed (campaign still in session cookie):', e); }
        } else if (DEBUG) {
            var existingCampaign = null;
            try { existingCampaign = sessionStorage.getItem('fm_utk_campaign'); } catch (e) {}
            if (existingCampaign) dbg('campaign tag carried from earlier in session:', existingCampaign);
        }

        if (emailParam || idParam || campaignParam) {
            stripParamsFromUrl(['fm_utk_email', 'c_id', 'cmp', 'fm_utk_campaign']);
        }
    })();

    // 1. Identify the current fm-subs-authoriser subscriber on every page load.
    //    Modern klaviyo.js identify keys are unprefixed (email / first_name /
    //    last_name); custom properties are top-level on the same object.
    if (events.identifySubscriber !== false && ctx.subscriber && ctx.subscriber.email) {
        var s = ctx.subscriber;
        var identifyPayload = { email: s.email };
        if (s.firstName) identifyPayload.first_name = s.firstName;
        if (s.lastName) identifyPayload.last_name = s.lastName;
        if (s.activeSubscriptions) identifyPayload.ActiveSubscriptions = s.activeSubscriptions;
        if (s.subscriptionExpires != null) identifyPayload.SubscriptionExpires = s.subscriptionExpires;
        if (s.subscriptionStatus) identifyPayload.SubscriptionStatus = s.subscriptionStatus;
        if (ctx.site) identifyPayload.$append = { Sites: ctx.site };
        dbg('identifying subscriber from FM_UTK.subscriber:', s.email);
        safeIdentify(identifyPayload);
    } else if (DEBUG) {
        if (!ctx.subscriber) dbg('no subscriber in FM_UTK (anonymous visitor)');
        else if (!ctx.subscriber.email) dbg('subscriber object present but no email');
        else if (events.identifySubscriber === false) dbg('identifySubscriber event disabled in settings');
    }

    // 2. Drain the one-shot identify cookie set by hooks/form-submitted.php.
    var pending = readCookie('fm_kt_pending_identify');
    if (pending) {
        try {
            var pendingEmail = atob(pending);
            if (pendingEmail) {
                dbg('draining pending-identify cookie:', pendingEmail);
                var pendingPayload = { email: pendingEmail };
                if (ctx.site) pendingPayload.$append = { Sites: ctx.site };
                safeIdentify(pendingPayload);
            }
        } catch (e) { dbg('pending-identify cookie malformed, ignoring'); }
        deleteCookie('fm_kt_pending_identify');
    }

    // 3. Enriched page-view event. Branches on pageType so each WP view type
    //    becomes a distinct Klaviyo metric — segments like "viewed term
    //    'Restaurants' 3+ times in 30 days" only work if term views are their
    //    own event name. Common geo/UA/first-touch props are merged into all.
    //    When a `?c_id=` identity is still resolving, this is deferred to the
    //    resolve callback (see identityPending) so the view is attributed to the
    //    resolved profile instead of the anonymous one.
    if (!identityPending) {
        fireViewEventOnce();
    } else if (DEBUG) {
        dbg('view event deferred until c_id resolves (keeps landing attributed to the profile)');
    }

    function fireViewEvent(common) {
        // Home / blog index
        if (ctx.pageType === 'front' || ctx.pageType === 'home') {
            safeTrack('Viewed Home', Object.assign({
                HomeType: ctx.pageType === 'front' ? 'front' : 'blog_index',
                PageUrl: ctx.pageUrl,
                PageTitle: (ctx.post && ctx.post.title) || document.title,
            }, common));
            return true;
        }

        // Singular content — split between "Viewed Article" (post_type=post)
        // and "Viewed Page" (everything else: pages, custom post types).
        if (ctx.pageType === 'singular' && ctx.post) {
            var p = ctx.post;
            var eventName = p.post_type === 'post' ? 'Viewed Article' : 'Viewed Page';
            safeTrack(eventName, Object.assign({
                PostId: p.ID,
                PostType: p.post_type,
                Title: p.title,
                Categories: p.categories || [],
                PrimaryTag: p.primaryTag || null,
                Author: p.author || null,
                PublishedAt: p.publishedAt || null,
                PageUrl: p.permalink || ctx.pageUrl,
            }, common));
            return true;
        }

        // Taxonomy term page (category, tag, custom taxonomy).
        if (ctx.pageType === 'taxonomy' && ctx.term) {
            var t = ctx.term;
            safeTrack('Viewed Term', Object.assign({
                TermId: t.term_id,
                TermName: t.name,
                TermSlug: t.slug,
                Taxonomy: t.taxonomy,
                TermDescription: t.description || null,
                TermPostCount: t.count,
                PageUrl: t.permalink || ctx.pageUrl,
            }, common));
            return true;
        }

        // Archive (post-type archive, author archive, date archive, catch-all).
        if (ctx.archive) {
            var a = ctx.archive;
            var archiveProps = {
                ArchiveSubtype: a.subtype,
                ArchiveTitle: a.title || null,
                PageUrl: ctx.pageUrl,
            };
            if (a.post_type)       archiveProps.PostType       = a.post_type;
            if (a.post_type_label) archiveProps.PostTypeLabel  = a.post_type_label;
            if (a.author_id)       archiveProps.AuthorId       = a.author_id;
            if (a.author_name)     archiveProps.AuthorName     = a.author_name;
            if (a.author_slug)     archiveProps.AuthorSlug     = a.author_slug;
            if (a.year)            archiveProps.ArchiveYear    = a.year;
            if (a.month)           archiveProps.ArchiveMonth   = a.month;
            if (a.day)             archiveProps.ArchiveDay     = a.day;
            safeTrack('Viewed Archive', Object.assign(archiveProps, common));
            return true;
        }

        // Search results page.
        if (ctx.pageType === 'search' && ctx.search) {
            safeTrack('Viewed Search', Object.assign({
                SearchQuery: ctx.search.query || '',
                ResultsCount: ctx.search.results_count,
                PageUrl: ctx.pageUrl,
            }, common));
            return true;
        }

        // 404 page.
        if (ctx.pageType === '404') {
            safeTrack('Viewed 404', Object.assign({
                PageUrl: ctx.pageUrl,
                AttemptedPath: location.pathname,
                Referrer: document.referrer || null,
            }, common));
            return true;
        }

        return false;
    }

    // 4. Form-submit listener — layered on top of fm-form's existing markup
    //    (.dpl-form-box wraps every fm-form form, per
    //    /wp-content/plugins/fm-form/patterns/form-box/form-box.twig:11).
    if (events.submittedForm !== false) {
        document.addEventListener('submit', function (e) {
            var form = e.target;
            if (!form || !form.matches) return;
            if (!form.closest || !form.closest('.dpl-form-box')) {
                dbg('submit on non-fm-form element, ignoring:', form);
                return;
            }

            var emailInput = form.querySelector('input[type="email"]');
            if (!emailInput || !emailInput.value) {
                dbg('fm-form submit detected but no email input found — skipping identify');
                return;
            }

            var email = emailInput.value.trim();
            if (!email) return;

            dbg('fm-form submit detected, email:', email, 'form:', form);
            var formIdentifyPayload = { email: email };
            if (ctx.site) formIdentifyPayload.$append = { Sites: ctx.site };
            safeIdentify(formIdentifyPayload);
            var submitProps = {
                FormId: form.id || null,
                FormName: form.name || null,
                PageUrl: location.href,
                PageTitle: document.title,
                Source: 'js',
            };
            Object.assign(submitProps, firstTouch, siteProps(), campaignProps());
            safeTrack('Submitted Form', submitProps);
        }, true);
        dbg('form-submit listener installed');
    }

    dbg('boot complete (' + pendingQueue.length + ' call(s) queued, waiting for klaviyo.js)');
})();
